import {useRef} from 'react';
import {Form, Formik, FormikProps, useFormikContext} from 'formik';
import {useTranslation} from 'react-i18next';
import {Helper} from '@tickchak/react-share/app';
import InnerPop from '../../../../component/newPopup/innerPop/InnerPop';
import Input from '../../../../component/input/Input';
import NumberInput from '../../../../component/input/NumberInput';
import InputList from '../../../../component/input/inputList/InputList';
import {TShow} from '../../../../component/prompotionPopup/PromotionPopup';
import * as css from './PricingPop.css';
import * as pricingStepCss from '../createSubscriptionPopup/steps/PricingStep/PricingStep.css';
import {getPricingValidationSchema} from './PricingPop.validation';
import SingleTicketIcon from '../../../../assets/icons/SingleTicketIcon';
import CoupleTicketsIcon from '../../../../assets/icons/CoupleTicketsIcon';
import FamilyTicketsIcon from '../../../../assets/icons/FamilyTicketsIcon';
import CustomTicketsIcon from '../../../../assets/icons/CustomTicketsIcon';
import {Price} from '../../../../redux/subscription/subscription.type';
import {SubscriptionFormValues} from '../createSubscriptionPopup/createSubscriptionForm.types';

export interface PricingFormValues {
    title: string;
    price: string;
    ticketsAmount: number;
    selectedOption: number; // UI only - 1, 2, 4 = predefined, 0 = custom
}

function PricingPop({show, onSave, priceId}: {show: TShow, onSave?: (values: PricingFormValues) => void, priceId: string | null}) {
    const {t} = useTranslation('subscriptions');
    const {t: globalT} = useTranslation('global');
    const formRef = useRef<FormikProps<PricingFormValues>>(null);
    const {values} = useFormikContext<SubscriptionFormValues>();
    const prices = values.prices || [];
    const priceField = priceId ? prices.find((p: Price) => (p.tpid?.toString() || p.tempId) === priceId) : null;

    const ticketsAmountOptions = [
        {
            key: 1,
            text: t('singleTicketOption'),
            rightChildren: <SingleTicketIcon width={20} height={20} />
        },
        {
            key: 2,
            text: t('coupleTicketOption'),
            rightChildren: <CoupleTicketsIcon width={20} height={20} />
        },
        {
            key: 4,
            text: t('familyTicketOption'),
            rightChildren: <FamilyTicketsIcon width={20} height={20} />
        },
        {
            key: 0,
            text: t('customTicketOption'),
            rightChildren: <CustomTicketsIcon width={20} height={20} />
        }
    ];

    const validationSchema = getPricingValidationSchema(globalT);

    const getPredefinedOption = (value: number | null): number => {
        if(value && [1, 2, 4].includes(value)) return value;
        return 0; // custom
    };

    const initialTicketsAmount = priceField?.ticketsAmount ?? null;
    
    const initialValues: PricingFormValues = {
        title: priceField?.title ?? null,
        price: priceField?.price?.toString() ?? null,
        ticketsAmount: initialTicketsAmount,
        selectedOption: getPredefinedOption(initialTicketsAmount)
    };

    const handleSave = (values: PricingFormValues) => {
        if(onSave) {
            const {selectedOption, ...dataToSave} = values;
            onSave(dataToSave as PricingFormValues);
        }
        show.close();
    };

    const handleCancel = () => {
        formRef.current?.resetForm();
        show.close();
    };

    return (
        <css.pricingPop>
            <Formik
                innerRef={formRef}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSave}
            >
                {({values, setFieldValue}) => {
                    const isCustom = values.selectedOption === 0;
                    
                    const handleOptionChange = (name, value) => {
                        const option = Number(value);
                        setFieldValue('selectedOption', option);
                        if(option !== 0) {
                            setFieldValue('ticketsAmount', option);
                        }
                    };

                    return (
                        <Form placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                            <InnerPop
                                dataCy='pricing'
                                show={show}
                                title={t('pricingPopTitle')}
                                buttons={{
                                    whiteBtn: {
                                        text: t('pricingPopClose'),
                                        onClick: handleCancel
                                    },
                                    blueBtn: {
                                        text: t('pricingPopSave'),
                                        onClick: () => formRef.current?.submitForm()
                                    }
                                }}
                            >
                                <css.header />
                                <css.content>
                                    <Input
                                        name='title'
                                        text={t('pricingPopName')}
                                        description={t('pricingNameDescription')}
                                        icon={null}
                                        events={{}}
                                        className=''
                                        forwardRef={null}
                                    />
                                    <NumberInput
                                        name='price'
                                        text={t('price')}
                                        icon={null}
                                        className=''
                                        forwardRef={null}
                                    />
                                    <InputList
                                        name='selectedOption'
                                        list={ticketsAmountOptions}
                                        text={t('ticketsAmount')}
                                        description={isCustom ? '' : t('ticketsAmountDescription', {amount: values.ticketsAmount})}
                                        icon={null}
                                        className=''
                                        valid={true}
                                        onFocus={() => {}}
                                        onChange={handleOptionChange}
                                        listProps={{isFixed:true, width: '241px'}}
                                    >
                                        {''}
                                    </InputList>
                                    <pricingStepCss.localInputWrapper>
                                        <NumberInput
                                            name='ticketsAmount'
                                            text={t('manualTicketsAmount')}
                                            icon={null}
                                            className=''
                                            forwardRef={null}
                                            disabled={!isCustom}
                                        />
                                        <Helper
                                            title={t('manualTicketsAmountHelperTitle')}
                                            content={<pricingStepCss.helperContent>{t('manualTicketsAmountHelperContent')}</pricingStepCss.helperContent>}
                                            size='small'
                                        />
                                    </pricingStepCss.localInputWrapper>
                                </css.content>
                            </InnerPop>
                        </Form>
                    );
                }}
            </Formik>
        </css.pricingPop>
    );
}

export default PricingPop;

